import { useSubscription } from '@meraki-internal/state';
import { DrawerFieldText } from '../../components/drawer/DrawerFieldText.tsx';
import { DrawerLabel } from '../../components/drawer/DrawerLabel.tsx';
import { NewMissionState } from '../../missions/model/NewMissionState.ts';
import { MissionTimesEditor } from '../../missions/components/MissionTimesEditor.tsx';
import { FlightChecklist } from '../../flight-checklist/FlightChecklist.tsx';
import { FlightChecklistError } from '../../flight-checklist/advisories/FlightChecklistError.tsx';
import { MissionPinAndRadiusOnMapViewModel } from '../mission-pin-and-radius/MissionPinAndRadiusOnMapViewModel.ts';
import { Location } from './Location.tsx';
import { usePageView } from '../../support/tracking/usePageView.tsx';
import { DrawerButtons } from '../../components/drawer/DrawerButtons.tsx';
import { Button } from '../../components/button/Button.tsx';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { LastAuthorizedRadiusModel } from '../mission-pin-and-radius/LastAuthorizedRadiusModel.ts';
import { FlightChecklistDrawerLabel } from '../../components/drawer/FlightChecklistDrawerLabel.tsx';
import { PreMissionWeatherInDrawer } from '../../weather/components/PreMissionWeatherInDrawer.tsx';
import { RevenueCatModel } from '../../paywall/revenue-cat/RevenueCatModel.ts';
import { PreMissionWeatherUnlockButton } from '../../weather/components/PreMissionWeatherUnlockButton.tsx';
import React from 'react';
import { LatLngDrawerField } from './LatLngDrawerField.tsx';

const MissionHeader: React.FC<{vm: NewMissionState}> = ({ vm }) => {
    return (
        <>
            <DrawerLabel text="Location" />
            <DrawerFieldText dataSet={{type: 'location'}}><Location mission={vm} /></DrawerFieldText>

            <MissionTimesEditor mission={vm} source='Mission Planning Weather' />
        </>
    );
};

export const MissionAreaAndTimePage: React.FC<{vm: NewMissionState, onBack: () => void, onContinue: () => void}> = ({ vm, onBack, onContinue }) => {
    usePageView('Mission Area and Time');

    useSubscription(() => vm);
    const lastAuthorizedModel = useInstance(LastAuthorizedRadiusModel);
    useSubscription(() => lastAuthorizedModel);

    const pinAndRadius = useInstance(MissionPinAndRadiusOnMapViewModel);
    useSubscription(() => pinAndRadius);

    const missionRadius = pinAndRadius.getAuthorizedRadius();

    const isReady = Boolean(missionRadius || lastAuthorizedModel.lastAuthorizedRadius);
    const disabled = (!missionRadius || missionRadius.canFly() === 'cannot-fly');

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const hasPreMissionWeather = revenueCat.hasUpgraded();

    const mapCenter = vm.mapViewModel.getCenter();

    const location = vm.getLocation();

    return (
        <div data-id="mission-time-drawer">

            <DrawerLabel text="Location" />
            <DrawerFieldText dataSet={{type: 'location'}}><Location mission={vm} /></DrawerFieldText>

            <LatLngDrawerField location={location} trackingEvent='Mission Area | Copy Lat Lng' />

            {vm.getLocation() &&
                <>
                    <MissionTimesEditor mission={vm} source='Mission Area and Time' />

                    <div style={{position: 'relative', marginTop: -3, zIndex: 2}}>
                        <DrawerLabel text="Weather" />
                    </div>
                    <div style={{marginTop: -10, height: 42}}>
                        {!hasPreMissionWeather &&
                            <PreMissionWeatherUnlockButton
                                returnTo={`/map?dropPin=${mapCenter.lng},${mapCenter.lat}`}
                            />
                        }
                        {hasPreMissionWeather &&
                            <PreMissionWeatherInDrawer
                                location={vm.getLocation()!}
                                startTime={vm.getStartTime()}
                                endTime={vm.getEndTime()}
                                header={<MissionHeader vm={vm} />}
                            />
                        }
                    </div>
                </>
            }

            {isReady && (
                <div style={{position: 'relative'}}>
                    <FlightChecklistDrawerLabel />

                    {!missionRadius && pinAndRadius.authorizeFailed &&
                        <FlightChecklistError />
                    }

                    <div style={{ opacity: !missionRadius ? .5 : 1 }}>
                        <FlightChecklist mission={vm} />
                    </div>
                </div>
            )}

            <DrawerButtons>
                <Button color="secondary" fill="outline" dataType="cancel-button" onClick={onBack}>Cancel</Button>
                <Button dataType="continue-button" disabled={disabled} onClick={onContinue}>Continue</Button>
            </DrawerButtons>

        </div>
    );
};
